"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { FeaturedProjects } from "@/components/FeaturedProjects";
import { TypeWriter } from "@/components/TypeWriter";
import { ExperienceTimeline } from "@/components/ExperienceTimeline";
import { HeroScrollScene } from "@/components/HeroScrollScene";
import { ScrollRevealSection } from "@/components/ScrollRevealSection";
import { ScrollCard3D } from "@/components/ScrollCard3D";
import { MagneticButton } from "@/components/MagneticButton";
import { FloatingElements } from "@/components/FloatingElements";
import { ScrollProgressLine } from "@/components/ScrollProgressLine";
import { useProfile, useExperiences, useSkills } from "@/lib/use-portfolio-data";
import { MusicPlayer } from "@/components/MusicPlayer";
import { useGitHubStats } from "@/lib/use-github-stats";
import { useI18n } from "@/i18n/I18nProvider";

const TECH_CATEGORY_MAP: Record<string, { title: string; icon: string; color: string }> = {
  Backend: { title: "Backend", icon: "⚙️", color: "from-dev-blue to-dev-indigo" },
  Frontend: { title: "Frontend", icon: "🎨", color: "from-dev-cyan to-dev-emerald" },
  DevOps: { title: "DevOps & Tools", icon: "🚀", color: "from-dev-purple to-pink-500" },
};

export default function HomePage() {
  const [playerExpanded, setPlayerExpanded] = useState(false);
  const { profile, loading: profileLoading } = useProfile();
  const { data: experiences } = useExperiences();
  const { data: skills } = useSkills();
  const { t } = useI18n();

  const techCategories = Object.entries(
    skills.reduce<Record<string, string[]>>((acc, s) => {
      const cat = s.category || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(s.name);
      return acc;
    }, {})
  ).map(([cat, techs]) => ({
    ...(TECH_CATEGORY_MAP[cat] || { title: cat, icon: "📦", color: "from-gray-500 to-gray-400" }),
    techs,
  }));

  const { data: githubStats } = useGitHubStats(
    profile?.github_url,
    profile?.github_token
  );

  const stats =
    profile && githubStats
      ? [
        { key: "openSourceRepos", value: githubStats.public_repos, suffix: "+" },
        { key: "monthsOfExperience", value: profile.stat_months_exp, suffix: "+" },
        { key: "githubStars", value: githubStats.total_stars, suffix: "+" },
        { key: "commits", value: githubStats.total_commits, suffix: "+" },
      ]
      : profile
        ? [
          { key: "projects", value: profile.stat_projects, suffix: "+" },
          { key: "monthsOfExperience", value: profile.stat_months_exp, suffix: "+" },
          { key: "technologies", value: profile.stat_technologies, suffix: "+" },
          { key: "productionCommits", value: profile.stat_commits, suffix: "+" },
        ]
        : [];

  if (profileLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-dev-cyan border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      {/* ═══════════════════════════════════════════════════════
          HERO — Cinematic scroll-away: shrinks + tilts as you scroll
          ═══════════════════════════════════════════════════════ */}
      <HeroScrollScene>
        <section className="relative mx-auto max-w-6xl px-4 py-20 sm:py-28 lg:py-36 overflow-hidden">
          <FloatingElements />

          <div
            className={`flex flex-col items-center gap-10 transition-all duration-500 ${
              playerExpanded ? "lg:flex-row lg:justify-center" : "lg:flex-row lg:gap-16"
            }`}
          >
            <AnimatePresence mode="wait">
              {!playerExpanded && (
                <motion.div
                  key="hero-content"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, flex: 0, minWidth: 0, overflow: "hidden" }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="flex-1 text-center lg:text-left relative z-10 min-w-0 shrink-0"
                >
              <motion.p
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
                className="mb-4 font-mono text-sm tracking-widest text-dev-cyan uppercase"
              >
                {profile?.hero_greeting || t("home.heroGreetingDefault")}
              </motion.p>

              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                <motion.span
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] as const }}
                  className="text-dev-text-primary inline-block"
                >
                  Hey, I&apos;m{" "}
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
                  className="text-shimmer inline-block ml-3"
                >
                  {profile?.name || "Developer"}
                </motion.span>
              </h1>

              <div className="mt-5 h-10 font-mono text-lg text-dev-text-secondary sm:text-xl">
                <TypeWriter
                  words={profile?.roles?.length ? profile.roles : ["Developer"]}
                  typingSpeed={70}
                  deletingSpeed={40}
                  pauseTime={2500}
                />
              </div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
                className="mx-auto mt-5 max-w-xl text-dev-text-muted leading-relaxed lg:mx-0"
              >
                {profile?.hero_description || profile?.bio || ""}
              </motion.p>

              {stats.length > 0 && (
                <motion.ul
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
                  className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-dev-text-secondary lg:justify-start"
                >
                  {stats.slice(0, 3).map((stat) => (
                    <li key={stat.key} className="flex items-center gap-2">
                      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-dev-cyan" />
                      <span>
                        {stat.value}
                        {stat.suffix} {t(`home.stats.${stat.key}`)}
                      </span>
                    </li>
                  ))}
                </motion.ul>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
                className="mt-8 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start"
              >
                <MagneticButton strength={0.25}>
                  <Link
                    href="/projects/"
                    className="btn-gradient inline-flex items-center justify-center gap-2 px-7 py-3 text-base font-semibold"
                  >
                    <span>{t("common.viewProjects")}</span>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                </MagneticButton>
                <MagneticButton strength={0.2}>
                  <Link
                    href="/contact/"
                    className="clay-button inline-flex items-center justify-center gap-2 px-7 py-3 text-base font-medium text-dev-text-primary transition-colors hover:text-dev-cyan"
                  >
                    {t("common.getInTouch")}
                  </Link>
                </MagneticButton>
                {profile?.resume_url && (
                  <MagneticButton strength={0.2}>
                    <Link
                      href="/cv/"
                      className="btn-outline inline-flex items-center justify-center gap-2 px-7 py-3 text-base font-medium"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      {t("common.downloadCv")}
                    </Link>
                  </MagneticButton>
                )}
              </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Music Player / Avatar — vinyl disc với turntable */}
            <MusicPlayer
              avatarUrl={profile?.avatar_url ?? null}
              avatarAlt={profile?.name ?? "Avatar"}
              isExpanded={playerExpanded}
              onToggleExpand={() => setPlayerExpanded((v) => !v)}
            />
          </div>

          {/* Scroll indicator — ẩn khi player mở rộng */}
          {!playerExpanded && (
          <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="absolute bottom-6 left-1/2 -translate-x-1/2"
            >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-dev-text-muted"
            >
              <span className="text-xs tracking-wider">SCROLL</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </motion.div>
          </motion.div>
          )}
        </section>
      </HeroScrollScene>

      {/* ═══════════════════════════════════════════════════════
          EXPERIENCE — Slide up + scroll-drawing timeline
          ═══════════════════════════════════════════════════════ */}
      {experiences.length > 0 && (
        <ScrollRevealSection variant="fade-up">
          <section className="mx-auto max-w-6xl px-4 py-20">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-dev-text-primary sm:text-4xl heading-accent">
                {t("home.experienceTitle")}
              </h2>
              <p className="mt-5 text-dev-text-muted">{t("home.experienceSubtitle")}</p>
            </div>
            <div className="mx-auto max-w-3xl relative">
              <ScrollProgressLine />
              <ExperienceTimeline experiences={experiences} />
            </div>
          </section>
        </ScrollRevealSection>
      )}

      <div className="section-divider" />

      {/* ═══════════════════════════════════════════════════════
          TECH STACK — Zoom reveal + 3D parallax cards
          ═══════════════════════════════════════════════════════ */}
      {techCategories.length > 0 && (
        <ScrollRevealSection variant="zoom">
          <section className="mx-auto max-w-6xl px-4 py-20">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold text-dev-text-primary sm:text-4xl heading-accent">
                {t("home.techStackTitle")}
              </h2>
              <p className="mt-5 text-dev-text-muted">{t("home.techStackSubtitle")}</p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {techCategories.map((cat, i) => (
                <ScrollCard3D key={cat.title} index={i} total={techCategories.length}>
                  <div className="clay-card group p-6 h-full">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-2xl">{cat.icon}</span>
                      <h3 className={`text-lg font-semibold bg-gradient-to-r ${cat.color} bg-clip-text text-transparent`}>
                        {cat.title}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {cat.techs.map((tech) => (
                        <span key={tech} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </ScrollCard3D>
              ))}
            </div>
          </section>
        </ScrollRevealSection>
      )}

      <div className="section-divider" />

      {/* ═══════════════════════════════════════════════════════
          FEATURED PROJECTS — Slide from left
          ═══════════════════════════════════════════════════════ */}
      <ScrollRevealSection variant="slide-left">
        <section className="mx-auto max-w-6xl px-4 py-20">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-dev-text-primary sm:text-4xl heading-accent">
              {t("home.featuredProjectsTitle")}
            </h2>
            <p className="mt-5 text-dev-text-muted">{t("home.featuredProjectsSubtitle")}</p>
          </div>
          <FeaturedProjects />
          <div className="mt-10 text-center">
            <MagneticButton strength={0.2}>
              <Link
                href="/projects/"
                className="group inline-flex items-center gap-2 font-medium text-dev-cyan transition-all duration-200 hover:text-dev-blue-light hover:gap-3"
              >
                {t("home.featuredProjectsCta")}
                <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </MagneticButton>
          </div>
        </section>
      </ScrollRevealSection>

      <div className="section-divider" />

      {/* ═══════════════════════════════════════════════════════
          CTA — Zoom reveal with glow
          ═══════════════════════════════════════════════════════ */}
      <ScrollRevealSection variant="zoom">
        <section className="mx-auto max-w-6xl px-4 py-20">
          <div className="clay-card cta-glow-bg mx-auto max-w-2xl p-10 text-center sm:p-14 gradient-border">
            <h2 className="text-3xl font-bold text-dev-text-primary sm:text-4xl">
              {t("home.ctaTitle")}
            </h2>
            <p className="mx-auto mt-4 max-w-md text-dev-text-muted">
              {t("home.ctaSubtitle")}
            </p>
            <div className="mt-8">
              <MagneticButton strength={0.3}>
                <Link
                  href="/contact/"
                  className="btn-gradient inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold"
                >
                  <span>{t("home.ctaButton")}</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </Link>
              </MagneticButton>
            </div>
          </div>
        </section>
      </ScrollRevealSection>
    </div>
  );
}
